/**
 * Permission Group Service
 * Phase 5: RBAC & Permission System - M4
 * 
 * Frontend API service for permission group management (company owner)
 */

import api from './api';

const permissionGroupService = {
  /**
   * Get company permission groups
   * @param {string} companyId - Optional: Filter by company ID (for system admin)
   * @returns {Promise<Array>} List of permission groups
   */
  getPermissionGroups: async (companyId = null) => {
    const params = new URLSearchParams();
    if (companyId && companyId !== 'all') {
      params.append('companyId', companyId);
    }
    const queryString = params.toString();
    const url = `/permission-groups${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data.data;
  },

  /**
   * Get permission group by ID
   * @param {String} groupId - Group ID
   * @returns {Promise<Object>} Permission group details
   */
  getPermissionGroupById: async (groupId) => {
    const response = await api.get(`/permission-groups/${groupId}`);
    return response.data.data;
  },

  /**
   * Create new permission group
   * @param {Object} groupData - { name, description, permissionIds }
   * @returns {Promise<Object>} Created permission group
   */
  createPermissionGroup: async (groupData) => {
    const response = await api.post('/permission-groups', groupData);
    return response.data.data;
  },

  /**
   * Update permission group
   * @param {String} groupId - Group ID
   * @param {Object} updates - Update data
   * @returns {Promise<Object>} Updated permission group
   */
  updatePermissionGroup: async (groupId, updates) => {
    const response = await api.patch(`/permission-groups/${groupId}`, updates);
    return response.data.data;
  },

  /**
   * Delete permission group
   * @param {String} groupId - Group ID
   * @returns {Promise<Object>} Success message
   */
  deletePermissionGroup: async (groupId) => {
    const response = await api.delete(`/permission-groups/${groupId}`);
    return response.data;
  },

  /**
   * Get user's permission groups (assigned + available)
   * @param {String} userId - User ID
   * @returns {Promise<Object>} { user, assigned, available }
   */
  getUserPermissionGroups: async (userId) => {
    const response = await api.get(`/permission-groups/user/${userId}`);
    return response.data.data;
  },

  /**
   * Assign permission groups to user
   * @param {String} userId - User ID
   * @param {Array} groupIds - Array of group IDs
   * @returns {Promise<Object>} Updated user with permissions
   */
  assignPermissionGroups: async (userId, groupIds) => {
    const response = await api.post(`/permission-groups/user/${userId}/assign`, { groupIds });
    return response.data.data;
  }
};

export default permissionGroupService;

